import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import React, { useContext, useEffect, useRef, useState } from 'react'
import AnDateField from '../../../components/DateTime/DateField'
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner'
import API from '../../../services/API'
import StaffSelector from '../../events/StaffSelector'
import { GlobalContext } from '../../../App'
import PictureAsPdf from '@mui/icons-material/PictureAsPdf'
import moment from 'moment'
import SmallModal from '../../../components/Modals/SmallModal'
import { useNavigate } from 'react-router-dom'
import PCOptions from '../ePCR/History/presentingComplaintOptions'

const presentingComplaintOptions = Object.keys(PCOptions).reduce(
  (accumulator, category) => {
    PCOptions[category]
      .slice() // don't mutate the source array
      .sort((a, b) => {
        const aLabel = typeof a === 'string' ? a : a.label
        const bLabel = typeof b === 'string' ? b : b.label
        return aLabel.localeCompare(bLabel)
      })
      .forEach((item) => {
        if (typeof item === 'string') {
          accumulator.push({ category, item, keywords: [] })
        } else {
          accumulator.push({
            category,
            item: item.label,
            keywords: item.keywords || [],
          })
        }
      })
    return accumulator
  },
  [],
)

const UploadPaperPRF = ({ editMode, setEditMode, existingRecord, loadExistingRecord }) => {
  const [incidentDate, setIncidentDate] = useState(moment())
  const [event, setEvent] = useState(null)
  const [prfNumber, setPrfNumber] = useState('')
  const [completedBy, setCompletedBy] = useState(null)
  const [staffInvolved, setStaffInvolved] = useState([])
  const [incidentType, setIncidentType] = useState('Event')
  const [ptOutcome, setPtOutcome] = useState('')
  const [presentingComplaint, setPresentingComplaint] = useState([])
  const [demographics, setDemographics] = useState({
    firstName: '',
    lastName: '',
    dob: null,
    dobUnknown: false,
  })
  const [documents, setDocuments] = useState([])
  const [noDocumentsError, setNoDocumentsError] = useState(false)

  const [eventOptions, setEventOptions] = useState([])

  const { setToast } = useContext(GlobalContext)

  const fileInput = useRef()
  const navigate = useNavigate()

  const [showSuccessPopup, setShowSuccessPopup] = useState(false)

  useEffect(() => {
    if (editMode && existingRecord) {
      setIncidentDate(moment(existingRecord?.incidentDate))
      existingRecord?.event?._id && setEvent(existingRecord?.event)
      existingRecord?.pcrNumber && setPrfNumber(existingRecord.pcrNumber)
      setCompletedBy(existingRecord.completedBy)
      setStaffInvolved(existingRecord.staffInvolved)
      setIncidentType(existingRecord.incident.incidentType)
      setPtOutcome(existingRecord.plan.ptOutcome)
      existingRecord?.incident?.presentingComplaint?.length > 0 &&
        setPresentingComplaint(existingRecord.incident.presentingComplaint)
      setDemographics(existingRecord.demographics)
      // setDocuments(existingRecord.documents)
    }
  }, [])

  useEffect(() => {
    API.post('/epcr/search/eventoptions', {
      startDate: moment(incidentDate).startOf('day').toDate(),
      endDate: moment(incidentDate).endOf('day').toDate(),
    }).then((res) => res.status === 200 && setEventOptions(res.data))
  }, [incidentDate])

  useEffect(() => {
    return () => {
      setIncidentDate(moment())
      setEvent(null)
      setCompletedBy(null)
      setStaffInvolved([])
      setIncidentType('Event')
      setPtOutcome('')
      setPresentingComplaint([])
      setDemographics({
        firstName: '',
        lastName: '',
        dob: null,
        dobUnknown: false,
      })
      setDocuments([])
    }
  }, [showSuccessPopup])

  const handleFileInput = (e) => {
    const file = e.target.files[0]
    const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB
    if (file?.size >= MAX_FILE_SIZE) {
      setToast({ message: 'Files must be <10mb in size!', severity: 'error' })
    } else {
      const fileType = file.name.split('.').splice(-1)[0]
      if (['jpeg', 'jpg', 'png', 'gif'].includes(fileType)) {
        file.localURL = URL.createObjectURL(file)
      } else if (fileType === 'pdf') {
        file.localICON = <PictureAsPdf sx={{ fontSize: 40 }} />
      } else {
        file.localICON = <DocumentScannerIcon sx={{ fontSize: 40 }} />
      }
      setDocuments((prev) => [...prev, file])
    }
  }

  const handleEdit = (e) => {
    e.preventDefault()
    API.put('/paper-pcr', {
      _id: existingRecord._id,
      incidentDate,
      event: event?._id,
      completedBy: completedBy._id,
      staffInvolved: staffInvolved.map((x) => x._id),
      demographics,
      pcrNumber: prfNumber,
      incident: {
        presentingComplaint,
        incidentType,
      },
      plan: {
        ptOutcome,
      },
    }).then((res) => {
      if (res.status === 200) {
        loadExistingRecord()
        setEditMode(false)
      }
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const uploadDocs = () =>
      new Promise((callback) => {
        let documentPaths = []
        if (documents.length === 0) callback()
        documents.forEach(async (document, index, array) => {
          await API.post('/gets3uploadurl', {
            folder: 'paperpcrs',
            extension: document.type.split('/')[1],
          }).then((res) => {
            API.put(res.data.url, document, {
              headers: {
                'Content-Type':
                  document.type.split('/')[1] === 'pdf'
                    ? 'application/pdf'
                    : 'multipart/form-data',
              },
            })
            documentPaths.push(res.data.key)
          })
          if (index === array.length - 1) callback(documentPaths)
        })
      })
    await uploadDocs().then(async (filePaths) => {
      API.post('/paper-pcr', {
        ...(existingRecord?._id ? { _id: existingRecord?._id } : {}),
        incidentDate,
        event: !!event ? event._id : null,
        completedBy: completedBy._id,
        staffInvolved: staffInvolved.map((x) => x._id),
        demographics,
        pcrNumber: prfNumber,
        incident: {
          presentingComplaint,
          incidentType,
        },
        plan: {
          ptOutcome,
        },
        documents: filePaths,
      }).then((res) => {
        if (res.status === 200) {
          setShowSuccessPopup(res.data)
        }
      })
    })
  }

  return (
    <Paper padding={1}>
      <Stack gap={1} component="form" onSubmit={editMode ? handleEdit : handleSubmit} padding={1}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Stack gap={1}>
              <Typography variant="title2">Upload a Paper PRF</Typography>
              <Typography variant="title3">Incident Details</Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} lg={4}>
            <AnDateField
              date={incidentDate || null}
              fullWidth
              onChange={(newDate) => setIncidentDate(newDate)}
              label="Incident Date"
              disableFuture
            />
          </Grid>
          <Grid item xs={12} lg={4}>
            <FormControl fullWidth>
              <InputLabel>Incident Type</InputLabel>
              <Select
                value={incidentType}
                disabled={!!event}
                onChange={(e) => setIncidentType(e.target.value)}
                name="type"
                label="Incident Type"
              >
                {[
                  'Event',
                  '999',
                  'Patient Transfer',
                  'Off Duty or Travelling',
                  'Other',
                ].map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} lg={4}>
            <Autocomplete
              value={event}
              disabled={incidentType !== 'Event'}
              options={eventOptions}
              onChange={(e, value) => {
                setEvent(value)
                setIncidentType('Event')
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Event"
                  required={incidentType === 'Event'}
                />
              )}
              getOptionLabel={(option) =>
                `${option.title} (${moment(option.startDate).format(
                  'Do MMM YYYY',
                )})`
              }
              getOptionKey={(option) => option._id}
              isOptionEqualToValue={(option, value) => option._id === value._id}
              noOptionsText="No events found for this date."
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="PRF Number"
              value={prfNumber}
              onChange={(e) => setPrfNumber(e.target.value)}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <StaffSelector
              value={completedBy}
              onChange={(e, value) => setCompletedBy(value)}
              label="Completed By"
              noPhoto
              required
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <StaffSelector
              value={staffInvolved}
              required={staffInvolved.length === 0}
              multiple
              onChange={(e, value) => setStaffInvolved(value)}
              label="Staff Involved"
              noPhoto
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <FormControl fullWidth>
              <Autocomplete
                options={presentingComplaintOptions}
                getOptionLabel={(option) => option?.item || ''}
                renderTags={(tags, getTagProps) => (
                  <Stack direction="row" flexWrap="wrap" gap={1}>
                    {tags.map((tag, index) => {
                      const { key, ...chipProps } = getTagProps({ index })
                      return <Chip key={key} label={tag} {...chipProps} />
                    })}
                  </Stack>
                )}
                groupBy={(option) => option.category}
                value={presentingComplaint}
                isOptionEqualToValue={(option, value) => {
                  return presentingComplaint
                    ? option.item === value
                    : '' === value
                }}
                multiple
                clearOnBlur
                onChange={(e, value) => {
                  setPresentingComplaint(value.map((x) => x?.item ?? x))
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Presenting Complaint"
                    required={presentingComplaint?.length === 0}
                  />
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} lg={6}>
            <FormControl fullWidth required={ptOutcome === ''}>
              <InputLabel>Patient Outcome</InputLabel>
              <Select
                value={ptOutcome ?? ''}
                onChange={(e) => setPtOutcome(e.target.value)}
                label="Patient Outcome"
              >
                <MenuItem value="Transported to Hospital">
                  Transported to Hospital
                </MenuItem>
                <MenuItem value="Discharged following Treatment">
                  Discharged following Treatment
                </MenuItem>
                <MenuItem value="Discharged - Advice Only">
                  Discharged - Advice Only
                </MenuItem>
                <MenuItem value="Left before Treatment">
                  Left before Treatment
                </MenuItem>
                <MenuItem value="Handed over to Ambulance Crew">
                  Handed over to Ambulance Crew
                </MenuItem>
                <MenuItem value="Refused Treatment or Transport">
                  Refused Treatment or Transport
                </MenuItem>
                <MenuItem value="A&E - Own Transport">
                  A&E - Own Transport
                </MenuItem>
                <MenuItem value="Referred - Other">Referred - Other</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="title3">Patient Demographics</Typography>
          </Grid>
          <Grid item xs={12} lg={4}>
            <TextField
              fullWidth
              value={demographics?.firstName}
              onChange={(e) =>
                setDemographics((prev) => ({
                  ...prev,
                  firstName: e.target.value,
                }))
              }
              label="First Name"
              autoComplete="off"
            />
          </Grid>
          <Grid item xs={12} lg={4}>
            <TextField
              fullWidth
              value={demographics?.lastName}
              onChange={(e) =>
                setDemographics((prev) => ({
                  ...prev,
                  lastName: e.target.value,
                }))
              }
              label="Last Name"
              autoComplete="off"
            />
          </Grid>
          <Grid item xs={12} lg={4}>
            <Stack direction="row" gap={2}>
              <Box flex={1}>
                <AnDateField
                  fullWidth
                  date={demographics?.dob || null}
                  disabled={demographics.dobUnknown}
                  onChange={(newDate) =>
                    setDemographics((prev) => ({
                      ...prev,
                      dob: newDate,
                    }))
                  }
                  label="Date of Birth"
                />
              </Box>
              <FormControlLabel
                label="DOB Unknown"
                control={
                  <Checkbox
                    checked={demographics.dobUnknown}
                    onChange={(e) =>
                      setDemographics((prev) => ({
                        ...prev,
                        dobUnknown: e.target.checked,
                        dob: e.target.checked ? null : prev.dob,
                      }))
                    }
                  />
                }
              />
            </Stack>
          </Grid>
        </Grid>
        {!editMode && <Stack gap={1}>
          <Typography variant="title3">Documents</Typography>
          {documents.length === 0 && (
            <Alert display="block" severity="warning">
              You must upload at least one Paper Record
            </Alert>
          )}
          <Box>
            <Button
              variant="contained"
              size="small"
              startIcon={<DocumentScannerIcon />}
              onClick={() => fileInput.current.click()}
            >
              Upload a Document
            </Button>
            <input
              ref={fileInput}
              type="file"
              style={{ display: 'none' }}
              accept={['image/*', 'application/pdf']}
              onChange={handleFileInput}
            />
          </Box>
          <Box display="flex" gap={2}>
            {documents.map((file, index) => (
              <Box
                key={index + file.name}
                component={Paper}
                padding={1}
                maxHeight={150}
                display="inline-flex"
                position="relative"
                minHeight={150}
                minWidth={100}
                justifyContent="center"
                alignItems="center"
              >
                {file.localURL && (
                  <Box
                    component="img"
                    src={file.localURL}
                    sx={{
                      objectFit: 'contain',
                      maxWidth: '100%',
                      maxHeight: '100%',
                    }}
                  />
                )}
                {file.localICON && (
                  <Box
                    display="flex"
                    flexDirection="column"
                    gap={2}
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Box>{file.localICON}</Box>
                    <Typography variant="caption">{file.name}</Typography>
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        </Stack>}
        <Button
          disabled={!editMode && documents.length === 0}
          variant="contained"
          color="success"
          type="submit"
        >
          Submit
        </Button>
        <SmallModal open={!!showSuccessPopup}>
          <Stack gap={1}>
            <Typography variant="title3" textAlign="center">
              Paper PCR successfully uploaded
            </Typography>
            <Button
              fullWidth
              variant="contained"
              color="success"
              onClick={() => setShowSuccessPopup(false)}
            >
              Upload another
            </Button>
            <Button
              fullWidth
              variant="contained"
              color="info"
              onClick={() =>
                navigate('/patient-report-forms/paper/' + showSuccessPopup)
              }
            >
              View the Record
            </Button>
            <Button
              fullWidth
              variant="contained"
              color="anBlue"
              onClick={() => navigate('/patient-report-forms')}
            >
              Search Records
            </Button>
          </Stack>
        </SmallModal>
      </Stack>
    </Paper>
  )
}

export default UploadPaperPRF
